<template>
  <CustomModal ref="modal" class="manage-client-dictionary-modal" title="Associer un client au répertoire français">
    <CustomForm ref="form" :inputs="associationInputs" :submit="buttons.associate">

      <!-- CLIENT -->
      <div class="card">
        <div class="client-name">{{client?.name || "-"}}</div>
        <div class="client-details">{{clientTypeLabel}}<template v-if="siren"> · SIREN {{sirenDisplay}}</template></div>
        <div v-if="client?.frenchDirectory" class="current-association">
          Association actuelle : <b>{{client.frenchDirectory.company?.formal_name || client.frenchDirectory.identifier}}</b>
        </div>
      </div>

      <div v-if="client?.type === CLIENT_TYPE_ENUM.UNKNOWN.value" class="notice warning">
        Le type du client est inconnu, veuillez le préciser <a class="clickable" @click="goToClient">(aller à la fiche client)</a>.
      </div>

      <!-- ENTREPRISE FRANCAISE : recherche automatique par SIREN -->
      <template v-if="requiresFrenchDirectory">
        <div v-if="!siren" class="notice warning">
          Le client n'a pas de SIRET, renseignez-le pour retrouver l'entreprise <a class="clickable" @click="goToClient">(aller à la fiche client)</a>.
        </div>
        <div v-else-if="!isSearching && !selectedCompany" class="notice warning">
          Aucune entreprise trouvée dans le répertoire pour le SIREN {{sirenDisplay}}. Vérifiez le SIRET du client <a class="clickable" @click="goToClient">(aller à la fiche client)</a>.
        </div>

        <template v-if="selectedCompany">
          <div class="company">
            <b>{{selectedCompany.formal_name}}</b>
            <span>{{selectedCompany.postcode}} {{selectedCompany.city}}</span>
          </div>

          <h3>Entrée électronique</h3>
          <div class="entries">
            <div v-if="!entries.length && !isSearching" class="empty">Aucune entrée électronique</div>
            <div v-for="entry in entries" :key="entry.identifier" class="item" :class="{selected:selectedEntry?.identifier===entry.identifier}" @click="selectedEntry=entry">
              <b>{{entry.identifier}}</b>
              <span :class="entry.is_active ? 'active' : 'inactive'">{{entry.is_active ? "Active" : "Inactive"}}</span>
            </div>
          </div>
        </template>
      </template>

      <div v-if="isAlreadyAssociated" class="notice info">Cette adresse électronique est déjà associée au client.</div>
      <div v-else class="footer">
        <CustomButton style="width: 200px" :params="buttons.associate" @click="associate"/>
      </div>
    </CustomForm>
  </CustomModal>
</template>


<script>
import CustomModal from "@/components/modals/CustomModal.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import { NotificationService } from "@vues/v-common";
import { FrenchDirectoryController } from "@/invoice-transmission/controllers/invoice_transmission/FrenchDirectoryController";
import { FrenchDirectoryCacheController } from "@/invoice-transmission/controllers/invoice_transmission/FrenchDirectoryCacheController";
import {CLIENT_TYPE_TO_ELECTRONIC_INVOICING_TYPE, ElectronicInvoicingType} from "@/invoice-transmission/utils";
import {CLIENT_TYPE_ENUM, CLIENT_TYPE_ENUM_ARRAY, SIREN_MASK, formatIdentifier} from "@/utils";
import router from "@/router";

export default {
  name:"ClientDictionaryAssociationModal",
  props:["callback"],
  components:{CustomModal,CustomForm,CustomButton},
  data(){
    return {
      client:undefined,
      siren:"",
      entries:[],
      selectedCompany:undefined,
      selectedEntry:undefined,
      isSearching:false,

      buttons:{
        associate:{text:"Associer", disabled: true}
      },
      // Formulaire d'association
      associationInputs:{
        // Le type n'est pas saisi dans ce formulaire mais conditionne l'association : vide (type inconnu), le bouton reste désactivé
        clientType:{label:"",value:"",required:true},
        selectedEntry:{value:"",required:false}
      },
      CLIENT_TYPE_ENUM
    };
  },
  computed:{
    requiresFrenchDirectory(){
      return this.client?.type === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value;
    },
    sirenDisplay(){
      return formatIdentifier(this.siren, SIREN_MASK);
    },
    clientTypeLabel(){
      return CLIENT_TYPE_ENUM_ARRAY.find(type => type.value === this.client?.type)?.label || CLIENT_TYPE_ENUM.UNKNOWN.label;
    },
    // L'adresse électronique proposée est déjà celle de l'association existante : rien à enregistrer
    isAlreadyAssociated(){
      const association = this.client?.frenchDirectory;
      if(!association || !this.electronicInvoicingType)
        return false;

      let identifier;
      if(this.electronicInvoicingType === ElectronicInvoicingType.E_INVOICING)
        identifier = this.selectedEntry?.identifier;
      else if(this.electronicInvoicingType === ElectronicInvoicingType.E_REPORTING_B2C)
        identifier = this.client.mail;
      else
        identifier = this.client.siret;

      return !!identifier && association.identifier === identifier;
    },
    electronicInvoicingType(){
      if(this.client?.type === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value)
        return ElectronicInvoicingType.E_INVOICING;
      if(this.client?.type === CLIENT_TYPE_ENUM.INTERNATIONAL_COMPANY.value)
        return ElectronicInvoicingType.E_REPORTING_INTERNATIONAL;
      if(this.client?.type === CLIENT_TYPE_ENUM.INDIVIDUAL.value)
        return ElectronicInvoicingType.E_REPORTING_B2C;
      return undefined;
    }
  },
  watch:{
    client:{
      immediate:true,
      handler(){
        this.associationInputs.selectedEntry.required=this.requiresFrenchDirectory;
      }
    },
    selectedEntry(){
      this.associationInputs.selectedEntry.value=this.selectedEntry?.identifier || "";
    }
  },
  methods:{
    async openModal(client) {
      this.client = client;
      this.siren = client.siret ? client.siret.substring(0, 9) : "";

      this.associationInputs.clientType.value = client.type !== CLIENT_TYPE_ENUM.UNKNOWN.value ? client.type : undefined;

      this.associationInputs.selectedEntry.required = this.requiresFrenchDirectory;
      this.associationInputs.selectedEntry.value = "";

      this.entries = [];
      this.selectedCompany = undefined;
      this.selectedEntry = undefined;

      this.$refs.modal.openModal();

      // Recherche automatique par SIREN : l'entreprise trouvée est sélectionnée et ses entrées électroniques chargées
      if (this.requiresFrenchDirectory && this.siren) {
        await this.loadCompany();
      }
    },
    goToClient() {
      // TODO Fonctionner avec un name et non un ID est dangeureux mais il faut gérer l'objet ObjectID via le ms-common pour faire ça
      router.push(`/clients?name=${this.client.name}#edit-client`);
    },
    closeModal() {
      this.$refs.modal.closeModal();
    },
    async loadCompany() {
      this.isSearching = true;
      this.$refs.form?.startLoading();

      const resp = await FrenchDirectoryController.listCompanies(`?${new URLSearchParams({siren: this.siren}).toString()}`);
      const companies = resp.errors ? [] : (resp.data || []);
      this.selectedCompany = companies.find(company => company.number === this.siren) || companies[0];

      if (this.selectedCompany) {
        const entriesResp = await FrenchDirectoryController.listEntriesOfCompany(this.selectedCompany.number);
        this.entries = entriesResp.errors ? [] : (entriesResp || []);
        // L'entrée déjà associée est présélectionnée
        this.selectedEntry = this.entries.find(entry => entry.identifier === this.client.frenchDirectory?.identifier);
      }

      this.$refs.form?.stopLoading();
      this.isSearching = false;
    },
    getElectronicInvoicingType(clientType) {
      const electronicInvoicingType = CLIENT_TYPE_TO_ELECTRONIC_INVOICING_TYPE[clientType];
      if (!electronicInvoicingType) {
        throw new Error(`Client type "${clientType}" is not recognized or cannot be mapped to an electronic invoicing type`);
      }
      return electronicInvoicingType;
    },
    getIdentifier() {
      const electronicInvoicingType = this.getElectronicInvoicingType(this.client.type);
      if (electronicInvoicingType === ElectronicInvoicingType.E_INVOICING) {
        return this.selectedEntry?.identifier;
      } else if (electronicInvoicingType === ElectronicInvoicingType.E_REPORTING_B2C) {
        return this.client.mail;
      } else if (electronicInvoicingType === ElectronicInvoicingType.E_REPORTING_INTERNATIONAL) {
        return this.client.siret;
      }
      throw new Error(`Client type "${this.client.type}" cannot be mapped to an electronic invoicing type`);
    },
    async associate() {
      this.$refs.form?.startLoading();

      const body = {
        clientId: this.client.id,
        siret: this.client.siret,
        electronicInvoicingType: this.electronicInvoicingType,
        siren: this.selectedCompany?.number,
        identifier: this.getIdentifier(),
        company: this.selectedCompany
      };

      const resp = await FrenchDirectoryCacheController.create(body);

      this.$refs.form?.stopLoading();

      if (resp.errors)
        return;


      (new NotificationService()).addNotification("success", "Association enregistrée.");

      this.closeModal();

      if (this.callback)
        this.callback();
    }
  }
}
</script>

<style lang="stylus" scoped>
h3
  margin 20px 0 10px

.card, .company
  background #f5f5f5
  padding 15px
  border-radius 8px
  display flex
  flex-direction column
  gap 4px

.company
  margin-top 15px

.client-name
  font-weight bold
  font-size 1.1em

.client-details, .current-association
  overflow hidden
  text-overflow ellipsis

.notice
  margin-top 15px
  padding 10px 15px
  border-radius 8px

.notice.warning
  background #fff4e0

.notice.info
  background #40513b14
  text-align center

.entries
  max-height 250px
  overflow auto

.item
  padding 10px
  border-radius 5px
  cursor pointer
  display flex
  justify-content space-between
  gap 10px

.item:hover
  background #eee

.item.selected
  background #40513b24

.active
  color green

.inactive
  color red

.empty
  text-align center
  padding 15px
  background #40513b14

.footer
  display flex
  justify-content center
  margin-top 20px

.clickable
  text-decoration underline
  cursor pointer
  font-weight normal
</style>


<style lang="stylus">
.manage-client-dictionary-modal .modal
  width 560px !important
</style>
